import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import H2 from '../../components/Titles/h2';
import Link from '../../components/Links/link';
import ImageBackground from '../../components/Images/image-background';
import Icon from '../../components/Images/icon';
import H3 from '../../components/Titles/h3';
import DescriptionSplit from '../../components/Description/description-split';

import Opaline from '../../assets/images/opaline/house.jpg';

const homeIconsContext = require.context('../../assets/icons/opaline', false, /\.svg$/);

const homeIcons = homeIconsContext.keys().reduce((acc, key) => {
    const iconName = key.replace('./', '').replace('.svg', '');
    const iconModule = homeIconsContext(key);

    acc[iconName] = iconModule.default || iconModule;

    return acc;
}, {});

const OpalineFooter = () => {
    const { t } = useTranslation();

    const sectionData = t('global.sectionOpaline', { returnObjects: true });
    const {
        title,
        subdescription,
        description,
        discover,
        icons = [],
    } = sectionData;

    const loadedIcons = useMemo(() => {
        const iconsMap = {};

        icons.forEach((item) => {
            iconsMap[item.icon] = homeIcons[item.icon] || null;

            if (!homeIcons[item.icon]) {
                console.error(`Icon not found: ${item.icon}.svg`);
            }
        });

        return iconsMap;
    }, [icons]);

    return (
        <section className="relative px-4 sm:px-6 md:px-10 lg:px-12 py-10 md:py-16 overflow-hidden">
            <div className="absolute bottom-0 left-0 w-full h-[120px] md:h-[150px] bg-secondary" />

            <div className="relative max-w-[1450px] mx-auto border border-[#d4b06a] bg-[#f5f5f3] shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
                    <div className="bg-[#f7f7f5] px-6 py-8 sm:px-10 md:px-14 md:py-12 lg:px-14 lg:py-14 flex flex-col justify-center">
                        <div className="mb-6 md:mb-8">
                            <H2
                                title={title}
                                theme="description"
                                fontWeight="normal"
                                className="tk-grafolita-script !text-[28px] md:!text-[40px] leading-none tracking-[4px] md:tracking-[6px] text-[#6e7772] mb-5"
                            />

                            <H3
                                title={subdescription}
                                theme="secondary"
                                fontWeight="bold"
                                className="!text-[18px] md:!text-[22px] uppercase leading-tight text-[#0d2417] mb-6"
                            />
                        </div>

                        <div className="max-w-[640px]">
                            <DescriptionSplit
                                description={description}
                                className="text-[#6c736f] leading-[1.9] text-[15px] md:text-[18px]"
                            />
                        </div>

                        <div className="mt-8 md:mt-10">
                            <Link
                                link="https://lamaisonopaline.com"
                                externalLink={true}
                                textColor="main"
                                disableHoverEffect={true}
                            >
                                <span className="inline-flex items-center justify-center bg-[#caa24f] text-white px-6 md:px-8 py-3 md:py-4 text-[14px] md:text-[16px] shadow-sm hover:opacity-90 transition-opacity">
                                    {discover}
                                </span>
                            </Link>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 md:mt-12 max-w-[720px]">
                            {icons.map((item, index) => (
                                <div key={index} className="flex flex-col items-center text-center gap-2">
                                    {loadedIcons[item.icon] ? (
                                        <Icon
                                            icon={loadedIcons[item.icon]}
                                            alt={item.text}
                                            maxHeight="34px"
                                            maxWidth="34px"
                                            className="opacity-90 w-[34px] h-[34px]"
                                        />
                                    ) : (
                                        <div className="w-[34px] h-[34px] bg-gray-200" />
                                    )}

                                    <span className="text-[#1b211e] text-[15px] md:text-[17px] leading-snug">
                                        {item.text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative min-h-[320px] md:min-h-[500px] lg:min-h-full">
                        <div className="absolute inset-4 md:inset-5 lg:inset-6 border border-main pointer-events-none z-10" />

                        <ImageBackground
                            imageUrl={Opaline}
                            alt={title}
                            customHeight="100%"
                            divClassName="relative h-full w-full min-h-[320px] md:min-h-[500px]"
                            className="h-full w-full object-cover"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default memo(OpalineFooter);